import type { Camera } from "./Camera"
import { hasGestureIntent, VelocityTracker } from "./gesture"
import type { QuadTree } from "./QuadTree"
import type { Scene } from "./Scene"
import type { GraphCanvasCallbacks } from "./types"

const CLICK_TIME = 300

interface ActivePointer {
  x: number
  y: number
  pointerType: string
}

export class Interaction {
  private activePointers = new Map<number, ActivePointer>()
  private primaryPointerId: number | null = null
  private downX = 0
  private downY = 0
  private downTime = 0
  private lastX = 0
  private lastY = 0
  private dragging = false
  private tapEligible = false
  private currentHoverId: string | null = null
  private velocity = new VelocityTracker()
  private lastPinchDistance = 0
  private lastPinchX = 0
  private lastPinchY = 0

  constructor(
    private element: HTMLElement,
    private camera: Camera,
    private quadtree: QuadTree,
    private scene: Scene,
    private callbacks: GraphCanvasCallbacks,
    private markDirty: (interacting?: boolean) => void,
  ) {
    element.style.touchAction = "none"
    element.addEventListener("pointerdown", this.onPointerDown)
    element.addEventListener("pointermove", this.onPointerMove)
    element.addEventListener("pointerup", this.onPointerUp)
    element.addEventListener("pointercancel", this.onPointerCancel)
    element.addEventListener("pointerleave", this.onPointerLeave)
    element.addEventListener("wheel", this.onWheel, { passive: false })
  }

  destroy() {
    const el = this.element
    el.removeEventListener("pointerdown", this.onPointerDown)
    el.removeEventListener("pointermove", this.onPointerMove)
    el.removeEventListener("pointerup", this.onPointerUp)
    el.removeEventListener("pointercancel", this.onPointerCancel)
    el.removeEventListener("pointerleave", this.onPointerLeave)
    el.removeEventListener("wheel", this.onWheel)
    el.style.touchAction = ""
  }

  private onPointerDown = (event: PointerEvent) => {
    if (event.pointerType === "mouse" && event.button !== 0) return
    event.preventDefault()
    this.camera.stop()
    this.activePointers.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY,
      pointerType: event.pointerType,
    })
    this.element.setPointerCapture(event.pointerId)
    this.element.style.cursor = "grabbing"

    if (this.activePointers.size === 1) {
      this.primaryPointerId = event.pointerId
      this.downX = event.clientX
      this.downY = event.clientY
      this.downTime = event.timeStamp
      this.lastX = event.clientX
      this.lastY = event.clientY
      this.dragging = false
      this.tapEligible = true
      this.velocity.reset(event.clientX, event.clientY, event.timeStamp)
    } else {
      this.dragging = true
      this.tapEligible = false
      this.setPinchBaseline()
    }
    this.markDirty(true)
  }

  private onPointerMove = (event: PointerEvent) => {
    const pointer = this.activePointers.get(event.pointerId)
    if (!pointer) {
      if (event.pointerType === "mouse") this.updateHover(event)
      return
    }

    event.preventDefault()
    pointer.x = event.clientX
    pointer.y = event.clientY

    if (this.activePointers.size >= 2) {
      this.updatePinch()
      return
    }
    if (event.pointerId !== this.primaryPointerId) return

    const totalX = event.clientX - this.downX
    const totalY = event.clientY - this.downY
    const becameDrag = !this.dragging && hasGestureIntent(totalX, totalY)
    if (becameDrag) {
      this.dragging = true
      this.tapEligible = false
      // Catch the canvas up to the pointer once intent is unambiguous, then
      // subsequent moves remain exactly 1:1.
      this.camera.pan(totalX, totalY)
    } else if (this.dragging) {
      this.camera.pan(event.clientX - this.lastX, event.clientY - this.lastY)
    }

    this.velocity.add(event.clientX, event.clientY, event.timeStamp)
    this.lastX = event.clientX
    this.lastY = event.clientY
    if (this.dragging) this.markDirty(true)
  }

  private onPointerUp = (event: PointerEvent) => {
    this.finishPointer(event, false)
  }

  private onPointerCancel = (event: PointerEvent) => {
    this.finishPointer(event, true)
  }

  private finishPointer(event: PointerEvent, cancelled: boolean) {
    const pointer = this.activePointers.get(event.pointerId)
    if (!pointer) return
    pointer.x = event.clientX
    pointer.y = event.clientY
    if (
      this.activePointers.size === 1 &&
      event.pointerId === this.primaryPointerId &&
      this.dragging
    ) {
      const dx = event.clientX - this.lastX
      const dy = event.clientY - this.lastY
      if (dx !== 0 || dy !== 0) this.camera.pan(dx, dy)
    }
    this.velocity.add(event.clientX, event.clientY, event.timeStamp)
    this.activePointers.delete(event.pointerId)
    if (this.element.hasPointerCapture(event.pointerId)) {
      this.element.releasePointerCapture(event.pointerId)
    }

    if (this.activePointers.size > 0) {
      const [id, remaining] = this.activePointers.entries().next().value as [
        number,
        ActivePointer,
      ]
      this.primaryPointerId = id
      this.downX = this.lastX = remaining.x
      this.downY = this.lastY = remaining.y
      this.downTime = event.timeStamp
      this.dragging = true
      this.tapEligible = false
      this.velocity.reset(remaining.x, remaining.y, event.timeStamp)
      this.setPinchBaseline()
      return
    }

    const wasDragging = this.dragging
    const wasTap =
      !cancelled &&
      this.tapEligible &&
      !wasDragging &&
      event.timeStamp - this.downTime < CLICK_TIME &&
      !hasGestureIntent(event.clientX - this.downX, event.clientY - this.downY)

    this.primaryPointerId = null
    this.dragging = false
    this.tapEligible = false
    this.element.style.cursor = this.currentHoverId ? "pointer" : "grab"

    if (wasDragging && !cancelled) {
      const releaseVelocity = this.velocity.velocity()
      this.camera.startMomentum(releaseVelocity.x, releaseVelocity.y)
      this.markDirty(true)
    } else if (wasTap) {
      this.commitTap(event.clientX, event.clientY)
    }
  }

  private setPinchBaseline() {
    if (this.activePointers.size < 2) return
    const [a, b] = [...this.activePointers.values()]
    this.lastPinchDistance = Math.hypot(b.x - a.x, b.y - a.y)
    this.lastPinchX = (a.x + b.x) / 2
    this.lastPinchY = (a.y + b.y) / 2
  }

  private updatePinch() {
    const [a, b] = [...this.activePointers.values()]
    const distance = Math.hypot(b.x - a.x, b.y - a.y)
    const midX = (a.x + b.x) / 2
    const midY = (a.y + b.y) / 2
    const rect = this.element.getBoundingClientRect()

    if (this.lastPinchDistance > 0 && distance > 0) {
      this.camera.zoomBy(
        distance / this.lastPinchDistance,
        midX - rect.left,
        midY - rect.top,
      )
      this.camera.pan(midX - this.lastPinchX, midY - this.lastPinchY)
      this.markDirty(true)
    }

    this.lastPinchDistance = distance
    this.lastPinchX = midX
    this.lastPinchY = midY
  }

  private updateHover(event: PointerEvent) {
    const rect = this.element.getBoundingClientRect()
    const nodeId = this.hitTest(
      event.clientX - rect.left,
      event.clientY - rect.top,
    )

    if (nodeId !== this.currentHoverId) {
      this.currentHoverId = nodeId
      this.callbacks.onNodeHover?.(nodeId)
      this.element.style.cursor = nodeId ? "pointer" : "grab"
      this.markDirty()
    }
  }

  private onPointerLeave = (event: PointerEvent) => {
    if (this.activePointers.has(event.pointerId)) return
    if (this.currentHoverId !== null) {
      this.currentHoverId = null
      this.callbacks.onNodeHover?.(null)
      this.markDirty()
    }
    this.element.style.cursor = "grab"
  }

  private onWheel = (event: WheelEvent) => {
    event.preventDefault()
    if (event.ctrlKey || event.metaKey) {
      const rect = this.element.getBoundingClientRect()
      this.camera.zoom(
        event.deltaY,
        event.clientX - rect.left,
        event.clientY - rect.top,
      )
    } else {
      this.camera.pan(-event.deltaX, -event.deltaY)
    }
    this.markDirty(true)
  }

  private commitTap(clientX: number, clientY: number) {
    const rect = this.element.getBoundingClientRect()
    const nodeId = this.hitTest(clientX - rect.left, clientY - rect.top)

    if (nodeId) {
      this.callbacks.onNodeClick?.(nodeId)
    } else {
      this.callbacks.onPaneClick?.()
    }
    this.markDirty()
  }

  // ── Hit test (scale-aware) ─────────────────────────

  private hitTest(sx: number, sy: number): string | null {
    const world = this.camera.screenToWorld(sx, sy)
    // At low zoom, dots are bigger than bounding boxes — expand hit radius.
    const scale = this.camera.scale
    const dotWorldRadius = Math.max(6, 4 / scale)
    const hitRadius = Math.max(dotWorldRadius, 10 / scale)
    const idx = this.quadtree.queryPoint(world.x, world.y, hitRadius)
    return idx === null ? null : this.scene.ids[idx]
  }
}
